import React, { useState, useEffect, useRef } from 'react';

const steps = [
  {
    id: 0,
    label: '01',
    title: 'Requirements',
    subtitle: 'Functional & Non-Functional',
    color: '#c0ff00',
    description: 'Before touching any architecture, nail down exactly what the system must do - and what constraints it must operate under. Most candidates skip this and end up designing the wrong thing entirely.',
    tags: ['Functional', 'Non-Functional', 'Constraints', 'Scope'],
    preview: {
      type: 'split',
      left: {
        heading: 'Functional',
        icon: '✦',
        items: ['Core user-facing features', 'Data read / write operations', 'Real-time vs batch needs', 'Third-party integrations'],
      },
      right: {
        heading: 'Non-Functional',
        icon: '◆',
        items: ['High availability (99.99%)', 'Low latency at scale', 'Consistency guarantees', 'Data retention policy'],
      }
    }
  },
  {
    id: 1,
    label: '02',
    title: 'Capacity Estimation',
    subtitle: 'Back-of-envelope math',
    color: '#60a5fa',
    description: "Know your scale before picking your tech. Back-of-the-envelope math tells you whether you need one server or a thousand - and signals to the interviewer that you think in systems.",
    tags: ['DAU', 'QPS', 'Storage', 'Bandwidth'],
    preview: {
      type: 'calc',
      lines: [
        { label: 'Daily Active Users (DAU)', value: '500M', highlight: false },
        { label: 'Requests / user / day', value: '× 20', highlight: false },
        { label: 'Total requests / day', value: '= 10B', highlight: true },
        { label: 'Queries per second (QPS)', value: '≈ 115K', highlight: true },
        { label: 'Avg payload size', value: '× 1 KB', highlight: false },
        { label: 'Storage needed / day', value: '≈ 10 TB', highlight: true },
      ]
    }
  },
  {
    id: 2,
    label: '03',
    title: 'API Design',
    subtitle: 'Define the contract',
    color: '#a78bfa',
    description: 'Design your APIs before drawing any boxes. This forces clarity on data models, access patterns, and exactly which services need to communicate - no ambiguity.',
    tags: ['REST', 'gRPC', 'Pagination', 'Auth'],
    preview: {
      type: 'api',
      endpoints: [
        { method: 'POST', path: '/resource/create', desc: 'Create a new resource', color: '#34d399' },
        { method: 'GET',  path: '/resource/{id}', desc: 'Fetch by identifier', color: '#60a5fa' },
        { method: 'GET',  path: '/feed?cursor={c}', desc: 'Paginated listing', color: '#a78bfa' },
        { method: 'DEL',  path: '/resource/{id}', desc: 'Soft delete resource', color: '#fb923c' },
      ]
    }
  },
  {
    id: 3,
    label: '04',
    title: 'High-Level Design',
    subtitle: 'Draw the architecture',
    color: '#f472b6',
    description: 'Now connect the boxes. With requirements and APIs locked in, the architecture flows naturally. Each component has one clear job - and you can defend every choice.',
    tags: ['Load Balancer', 'Cache', 'Message Queue', 'CDN'],
    preview: {
      type: 'arch',
      nodes: [
        { id: 'client', label: 'Client', color: '#c0ff00' },
        { id: 'lb', label: 'Load Balancer', color: '#60a5fa' },
        { id: 'api', label: 'API Layer', color: '#a78bfa' },
        { id: 'cache', label: 'Cache', color: '#f472b6' },
        { id: 'db', label: 'Database', color: '#fb923c' },
      ]
    }
  },
  {
    id: 4,
    label: '05',
    title: 'Deep Dive',
    subtitle: 'Where you win the interview',
    color: '#34d399',
    description: 'Pick the 2-3 hardest components and go deep - tradeoffs, failure modes, scaling bottlenecks. This is the part that separates a good answer from a great one.',
    tags: ['Sharding', 'Replication', 'Caching Strategy', 'Fault Tolerance'],
    preview: {
      type: 'deepdive',
      topics: [
        { title: 'Database Sharding', detail: 'Range-based vs hash-based - tradeoffs for your specific access pattern' },
        { title: 'Caching Strategy', detail: 'Write-through vs write-back vs write-around - when each makes sense' },
        { title: 'Handling Hot Spots', detail: 'Identify skewed load and redistribute without downtime' },
        { title: 'Failure & Recovery', detail: 'Circuit breakers, retries with backoff, graceful degradation' },
      ]
    }
  },
];

const PreviewPane = ({ step }) => {
  const p = step.preview;

  if (p.type === 'split') return (
    <div className="cs-preview-split">
      {[p.left, p.right].map((col, i) => (
        <div key={i} className="cs-split-col">
          <div className="cs-split-heading" style={{ color: step.color }}>
            <span>{col.icon}</span> {col.heading}
          </div>
          <ul className="cs-split-list">
            {col.items.map((item, j) => (
              <li key={j} className="cs-split-item" style={{ animationDelay: `${j * 80}ms` }}>
                <span className="cs-split-dot" style={{ background: step.color }} />
                {item}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );

  if (p.type === 'calc') return (
    <div className="cs-preview-calc">
      <div className="cs-calc-header">
        <span style={{ color: step.color }}>∑</span> Capacity Estimation
      </div>
      {p.lines.map((line, i) => (
        <div key={i} className={`cs-calc-row ${line.highlight ? 'highlight' : ''}`} style={{ animationDelay: `${i * 70}ms`, '--hl-color': step.color }}>
          <span className="cs-calc-label">{line.label}</span>
          <span className="cs-calc-value" style={line.highlight ? { color: step.color } : {}}>{line.value}</span>
        </div>
      ))}
    </div>
  );

  if (p.type === 'api') return (
    <div className="cs-preview-api">
      <div className="cs-api-header">
        <span style={{ color: step.color }}>{'{}'}</span> API Endpoints
      </div>
      {p.endpoints.map((ep, i) => (
        <div key={i} className="cs-api-row" style={{ animationDelay: `${i * 80}ms` }}>
          <span className="cs-api-method" style={{ color: ep.color, borderColor: `${ep.color}44`, background: `${ep.color}11` }}>{ep.method}</span>
          <span className="cs-api-path">{ep.path}</span>
          <span className="cs-api-desc">{ep.desc}</span>
        </div>
      ))}
    </div>
  );

  if (p.type === 'arch') return (
    <div className="cs-preview-arch">
      <div className="cs-arch-header"><span style={{ color: step.color }}>⬡</span> Architecture</div>
      <div className="cs-arch-flow">
        {p.nodes.map((node, i) => (
          <React.Fragment key={i}>
            <div className="cs-arch-node" style={{ '--node-color': node.color, animationDelay: `${i * 100}ms` }}>
              {node.label}
            </div>
            {i < p.nodes.length - 1 && (
              <div className="cs-arch-arrow" style={{ animationDelay: `${i * 100 + 80}ms` }}>→</div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );

  if (p.type === 'deepdive') return (
    <div className="cs-preview-deepdive">
      <div className="cs-dd-header"><span style={{ color: step.color }}>⚡</span> Deep Dive Topics</div>
      {p.topics.map((t, i) => (
        <div key={i} className="cs-dd-row" style={{ animationDelay: `${i * 80}ms` }}>
          <div className="cs-dd-title" style={{ color: step.color }}>{t.title}</div>
          <div className="cs-dd-detail">{t.detail}</div>
        </div>
      ))}
    </div>
  );

  return null;
};

const CourseStructure = () => {
  const [active, setActive] = useState(0);
  const [animKey, setAnimKey] = useState(0);
  const [autoPlay, setAutoPlay] = useState(false);
  const carouselRef = useRef(null);
  const navRef = useRef(null);

  useEffect(() => {
    if (!autoPlay) return;
    const t = setInterval(() => {
      setActive(prev => {
        const next = (prev + 1) % steps.length;
        setAnimKey(k => k + 1);
        return next;
      });
    }, 3000);
    return () => clearInterval(t);
  }, [autoPlay]);

  const handleSelect = (i) => {
    setActive(i);
    setAnimKey(k => k + 1);
    setAutoPlay(false);
    // On mobile, scroll carousel to selected card
    if (carouselRef.current) {
      const el = carouselRef.current;
      const card = el.firstChild;
      const cardWidth = card ? card.offsetWidth + 16 : el.scrollWidth / steps.length;
      el.scrollTo({ left: i * cardWidth, behavior: 'smooth' });
    }
  };

  // Sync nav + dots when user swipes carousel on mobile
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const onScroll = () => {
      const card = el.firstChild;
      const cardWidth = card ? card.offsetWidth + 16 : el.scrollWidth / steps.length;
      const idx = Math.min(Math.round(el.scrollLeft / cardWidth), steps.length - 1);
      if (idx >= 0 && idx < steps.length) {
        setActive(idx);
        // Scroll nav to show active button
        if (navRef.current) {
          const btn = navRef.current.children[idx];
          if (btn) btn.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const step = steps[active];

  return (
    <section className="course-structure" id="approach">
      <div className="section-header">
        <div className="section-badge">Our Approach</div>
        <h2 className="section-title">
          The 5-Step Interview <span className="text-gradient">Framework</span>
        </h2>
        <p className="section-subtitle">
          Every system design answer follows the same structure — learn it once, apply it everywhere.
        </p>
      </div>

      <div className="cs-interactive">
        {/* Step selector nav */}
        <div className="cs-steps-nav" ref={navRef}>
          {steps.map((s, i) => (
            <button
              key={i}
              className={`cs-step-btn ${active === i ? 'active' : ''}`}
              style={{ '--step-color': s.color }}
              onClick={() => handleSelect(i)}
            >
              <span className="cs-step-label">{s.label}</span>
              <div className="cs-step-info">
                <span className="cs-step-title">{s.title}</span>
                <span className="cs-step-sub">{s.subtitle}</span>
              </div>
              <div className="cs-step-progress" />
            </button>
          ))}
        </div>

        {/* Desktop: single animated panel */}
        <div className="cs-detail-panel cs-desktop-panel" key={animKey} style={{ '--panel-color': step.color }}>
          <div className="cs-detail-left">
            <div className="cs-detail-num">{step.label}</div>
            <h3 className="cs-detail-title" style={{ color: step.color }}>{step.title}</h3>
            <p className="cs-detail-desc">{step.description}</p>
            <div className="cs-detail-tags">
              {step.tags.map((tag, i) => (
                <span key={i} className="cs-tag" style={{ color: step.color, borderColor: `${step.color}40`, background: `${step.color}10` }}>{tag}</span>
              ))}
            </div>
          </div>
          <div className="cs-detail-right">
            <PreviewPane step={step} />
          </div>
        </div>

        {/* Mobile: swipable carousel — all cards rendered, scroll-snap */}
        <div className="cs-mobile-carousel" ref={carouselRef}>
          {steps.map((s, i) => (
            <div key={i} className="cs-detail-panel cs-carousel-card" style={{ '--panel-color': s.color }}>
              <div className="cs-detail-left">
                <div className="cs-detail-num">{s.label}</div>
                <h3 className="cs-detail-title" style={{ color: s.color }}>{s.title}</h3>
                <p className="cs-detail-desc">{s.description}</p>
                <div className="cs-detail-tags">
                  {s.tags.map((tag, ti) => (
                    <span key={ti} className="cs-tag" style={{ color: s.color, borderColor: `${s.color}40`, background: `${s.color}10` }}>{tag}</span>
                  ))}
                </div>
              </div>
              <div className="cs-detail-right">
                <PreviewPane step={s} />
              </div>
            </div>
          ))}
        </div>

        {/* Progress dots */}
        <div className="cs-dots">
          {steps.map((s, i) => (
            <button key={i} className={`cs-dot ${active === i ? 'active' : ''}`} style={{ '--dot-color': s.color }} onClick={() => handleSelect(i)} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CourseStructure;
